import { MessageCircle, Send, Globe, CheckCircle2, ArrowLeft, ChevronRight, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import WhatsAppEmbeddedSignupButton from '@/components/channels/WhatsAppEmbeddedSignupButton'
import { ENABLE_UNOFFICIAL_WHATSAPP_QR } from '@/pages/whatsapp/CreateWahaInstanceModal'
import type { ChannelType, ValidatedTelegramBot } from './types'

interface Step2Props {
  channelType: ChannelType
  instanceName: string
  botToken: string
  telegramReceiveMode: 'webhook' | 'polling'
  cloudPhoneNumberId: string
  cloudAccessToken: string
  validatingToken: boolean
  validatedTelegramBot: ValidatedTelegramBot | null
  loading: boolean
  existingInstances?: Array<{ id: string; channelType: ChannelType; instanceName: string; status: string }>
  selectedExistingId?: string | null
  onSelectExistingInstance?: (id: string) => void
  onContinueWithExisting?: () => void
  onChannelTypeChange: (type: ChannelType) => void
  onInstanceNameChange: (value: string) => void
  onBotTokenChange: (value: string) => void
  onTelegramReceiveModeChange: (mode: 'webhook' | 'polling') => void
  onCloudPhoneNumberIdChange: (value: string) => void
  onCloudAccessTokenChange: (value: string) => void
  onValidateTelegramToken: () => void
  onBack: () => void
  onNext: () => void
}

export default function Step2ChannelSetup(props: Step2Props) {
  const channelLabel = {
    TELEGRAM: 'Telegram',
    WEBCHAT: 'WebChat',
    WHATSAPP_CLOUD: 'WhatsApp Cloud API',
    WHATSAPP: 'WhatsApp (QR)',
  }[props.channelType]

  return (
    <div className="space-y-6 animate-slideInRight">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <MessageCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Conecta tu primer canal
        </h3>
        <p className="text-gray-600">
          Elige cómo quieres comunicarte con tus clientes
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        {props.existingInstances && props.existingInstances.length > 0 && (
          <div className="mb-6 rounded-2xl border-2 border-[#075f55]/20 bg-[#075f55]/5 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#075f55]">
                ✓ Canales detectados en tu cuenta
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                Guardado en BD
              </span>
            </div>
            <div className="space-y-2">
              {props.existingInstances.map((inst) => {
                const isSelected = props.selectedExistingId === inst.id
                return (
                  <div
                    key={inst.id}
                    onClick={() => props.onSelectExistingInstance?.(inst.id)}
                    className={`flex items-center justify-between p-3.5 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#075f55] bg-white shadow-sm ring-2 ring-[#075f55]/20'
                        : 'border-gray-200 bg-white/80 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#075f55]/10 text-[#075f55]">
                        <MessageCircle className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{inst.instanceName}</p>
                        <p className="text-xs text-gray-500">{inst.channelType} • Estado: {inst.status}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <span className="text-xs font-bold text-[#075f55] bg-[#075f55]/10 px-2.5 py-1 rounded-full">
                        Activo
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-[#075f55]/15">
              <p className="text-xs text-slate-600">
                Puedes avanzar con tu canal existente o crear uno nuevo abajo.
              </p>
              <Button
                type="button"
                onClick={props.onContinueWithExisting}
                className="w-full sm:w-auto bg-[#075f55] hover:bg-[#064e46] text-white text-xs font-bold px-4 h-9 shadow-sm"
              >
                Continuar con este canal <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {props.existingInstances && props.existingInstances.length > 0 && (
          <div className="relative my-6 text-center">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <span className="relative bg-[#f6f8f8] px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
              O configurar un canal nuevo
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {ENABLE_UNOFFICIAL_WHATSAPP_QR && (
            <button
              type="button"
              onClick={() => props.onChannelTypeChange('WHATSAPP')}
              className={`group relative rounded-xl border-2 p-5 text-left transition-all duration-200 ${
                props.channelType === 'WHATSAPP' 
                  ? 'border-green-500 bg-green-50 shadow-lg scale-105' 
                  : 'border-gray-200 hover:border-green-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2.5 rounded-lg ${props.channelType === 'WHATSAPP' ? 'bg-green-500' : 'bg-green-100 group-hover:bg-green-200'}`}>
                  <MessageCircle className={`h-6 w-6 ${props.channelType === 'WHATSAPP' ? 'text-white' : 'text-green-600'}`} />
                </div>
                {props.channelType === 'WHATSAPP' && (
                  <div className="bg-green-500 text-white rounded-full p-1">
                    <Check className="h-4 w-4" />
                  </div>
                )}
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">WhatsApp (QR)</h4>
              <p className="text-xs text-gray-600 mb-2">Conecta escaneando un código QR</p>
              <span className="inline-flex items-center text-xs font-medium text-green-600">
                Recomendado • Fácil
              </span>
            </button>
          )}

          <button
            type="button"
            onClick={() => props.onChannelTypeChange('WHATSAPP_CLOUD')}
            className={`group relative rounded-xl border-2 p-5 text-left transition-all duration-200 ${
              props.channelType === 'WHATSAPP_CLOUD' 
                ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105' 
                : 'border-gray-200 hover:border-emerald-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2.5 rounded-lg ${props.channelType === 'WHATSAPP_CLOUD' ? 'bg-emerald-500' : 'bg-emerald-100 group-hover:bg-emerald-200'}`}>
                <MessageCircle className={`h-6 w-6 ${props.channelType === 'WHATSAPP_CLOUD' ? 'text-white' : 'text-emerald-600'}`} />
              </div>
              {props.channelType === 'WHATSAPP_CLOUD' && (
                <div className="bg-emerald-500 text-white rounded-full p-1">
                  <Check className="h-4 w-4" />
                </div>
              )}
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">WhatsApp Cloud API</h4>
            <p className="text-xs text-gray-600 mb-2">Conexión oficial con Meta</p>
            <span className="inline-flex items-center text-xs font-medium text-emerald-600">
              Recomendado • Oficial Meta
            </span>
          </button>

          <button
            type="button"
            onClick={() => props.onChannelTypeChange('TELEGRAM')}
            className={`group relative rounded-xl border-2 p-5 text-left transition-all duration-200 ${
              props.channelType === 'TELEGRAM' 
                ? 'border-blue-500 bg-blue-50 shadow-lg scale-105' 
                : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2.5 rounded-lg ${props.channelType === 'TELEGRAM' ? 'bg-blue-500' : 'bg-blue-100 group-hover:bg-blue-200'}`}>
                <Send className={`h-6 w-6 ${props.channelType === 'TELEGRAM' ? 'text-white' : 'text-blue-600'}`} />
              </div>
              {props.channelType === 'TELEGRAM' && (
                <div className="bg-blue-500 text-white rounded-full p-1">
                  <Check className="h-4 w-4" />
                </div>
              )}
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Telegram</h4>
            <p className="text-xs text-gray-600 mb-2">Conecta con token de BotFather</p>
            <span className="inline-flex items-center text-xs font-medium text-blue-600">
              Popular
            </span>
          </button>

          <button
            type="button"
            onClick={() => props.onChannelTypeChange('WEBCHAT')}
            className={`group relative rounded-xl border-2 p-5 text-left transition-all duration-200 ${
              props.channelType === 'WEBCHAT' 
                ? 'border-gray-500 bg-gray-50 shadow-lg scale-105' 
                : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2.5 rounded-lg ${props.channelType === 'WEBCHAT' ? 'bg-gray-700' : 'bg-gray-100 group-hover:bg-gray-200'}`}>
                <Globe className={`h-6 w-6 ${props.channelType === 'WEBCHAT' ? 'text-white' : 'text-gray-700'}`} />
              </div>
              {props.channelType === 'WEBCHAT' && (
                <div className="bg-gray-700 text-white rounded-full p-1">
                  <Check className="h-4 w-4" />
                </div>
              )}
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">WebChat</h4>
            <p className="text-xs text-gray-600 mb-2">Widget para tu sitio web</p>
            <span className="inline-flex items-center text-xs font-medium text-gray-600">
              Integración web
            </span>
          </button>
        </div>

        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Nombre de la instancia <span className="text-red-500">*</span>
            </label>
            <Input
              value={props.instanceName}
              onChange={(e) => props.onInstanceNameChange(e.target.value)}
              placeholder={`Ej: ${channelLabel} principal`}
              className="h-11"
            />
          </div>

          {props.channelType === 'TELEGRAM' && (
            <div className="space-y-4 pt-2 border-t border-gray-200">
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Bot Token <span className="text-red-500">*</span>
                </label>
                <Input
                  value={props.botToken}
                  onChange={(e) => props.onBotTokenChange(e.target.value)}
                  placeholder="123456789:ABC..."
                  className="h-11 font-mono text-sm"
                />
                <p className="mt-1.5 text-xs text-gray-500">Obtén tu token desde @BotFather en Telegram</p>
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Modo de recepción</label>
                <select
                  value={props.telegramReceiveMode}
                  onChange={(e) => props.onTelegramReceiveModeChange(e.target.value as 'webhook' | 'polling')}
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="webhook">Webhook (Recomendado)</option>
                  <option value="polling">Long Polling</option>
                </select>
              </div>
              <Button
                variant="outline"
                onClick={props.onValidateTelegramToken}
                disabled={props.validatingToken || !props.botToken.trim()}
                className="w-full h-11"
              >
                {props.validatingToken ? 'Validando...' : 'Validar token'}
              </Button>
              {props.validatedTelegramBot && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-900">
                    Bot validado: <strong>@{props.validatedTelegramBot.username || '-'}</strong> ({props.validatedTelegramBot.firstName || '-'})
                  </p>
                </div>
              )}
            </div>
          )}

          {props.channelType === 'WHATSAPP_CLOUD' && (
            <div className="space-y-4 pt-2 border-t border-gray-200">
              <WhatsAppEmbeddedSignupButton
                onSuccess={() => {
                  props.onNext();
                }}
              />

              <details className="pt-2 text-xs text-gray-500">
                <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                  ¿Prefieres conectar manualmente con credenciales de desarrollador?
                </summary>
                <div className="space-y-4 pt-3 mt-2 border-t border-gray-200">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Phone Number ID <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={props.cloudPhoneNumberId}
                      onChange={(e) => props.onCloudPhoneNumberIdChange(e.target.value)}
                      placeholder="123456789012345"
                      className="h-11 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Access Token <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="password"
                      value={props.cloudAccessToken}
                      onChange={(e) => props.onCloudAccessTokenChange(e.target.value)}
                      placeholder="EAAxxxxxxxxxx..."
                      className="h-11 font-mono text-sm"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">Obtén tus credenciales desde Meta for Developers</p>
                  </div>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={props.onBack}
          disabled={props.loading}
          className="h-11 min-w-[120px]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
        <Button
          onClick={props.onNext}
          disabled={props.loading}
          className="h-11 min-w-[160px] bg-gradient-to-r from-[#075E54] to-[#128C7E] hover:from-[#064e46] hover:to-[#0f7a6e]"
        >
          {props.loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              Conectando...
            </>
          ) : (
            <>Conectar canal <ChevronRight className="ml-2 h-4 w-4" /></>
          )}
        </Button>
      </div>
    </div>
  )
}
